/** @jsx isolateComponent */

import { Fragment } from 'react';
import { css, keyframes } from '@emotion/core';
import { filter } from 'lodash';

import { isolateComponent, withModifier } from '@zapier/style-encapsulation';
import {
  Spacer,
  Text,
  Typography,
  Colors,
  BackButton as RebrandBackButton,
  Button,
  Grid,
} from '@zapier/design-system';

import Section from '../../shared/Section';
import { SizeType } from '../../../types';

type IntProps = {
  /** `true` if the user is eligible for premier support */
  isPremierSupportEligible: boolean;
  /** `true` if the user is eligible for email support */
  isEmailSupportEligible: boolean;
  /** `true` if there are Help Center articles related to the user's issue */
  hasArticles: boolean;
  /** `true` if there are Community conversations related to the user's issue */
  hasCommunityConversations: boolean;
  /** Called when the user clicks the 'back' button. */
  onClickBack: () => void;
  /** Called when the user clicks the 'next' button. */
  onClickNext: () => void;
  /** Called when the user clicks the 'skip to support' button. */
  onClickSkip: () => void;
  /** Size to display. */
  size?: SizeType;
};

type IntItem = {
  title: string;
  subtitle: string;
  condition: (props: IntProps) => boolean;
};

type IntListItemProps = {
  title: string;
  subtitle: string;
  index: number;
};

const ANIMATION_DURATION = 0.4;

// Each list item in the interstitial includes a `condition` function.
// The item is displayed if the condition evaluates to `true`.
const items: Array<IntItem> = [
  {
    title: 'Review our help articles',
    subtitle: 'We’ve found some relevant articles that may help',
    condition: ({ hasArticles }) => hasArticles,
  },
  {
    title: 'Check our Community conversations',
    subtitle: 'See if related conversations already answer your question',
    condition: ({ hasCommunityConversations }) => hasCommunityConversations,
  },
  {
    title: 'Or, email Zapier Support',
    subtitle: 'If those steps don’t help, write in to our support team',
    condition: ({ isEmailSupportEligible }) => isEmailSupportEligible,
  },
  {
    title: 'Or, upgrade for access to Zapier Support',
    subtitle:
      'If those steps don’t help, upgrade to write in to our support team',
    condition: ({ isEmailSupportEligible }) => !isEmailSupportEligible,
  },
];

const buildItems = (props: IntProps) =>
  filter(items, ({ condition }) => condition(props));

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// SXP-1559
// This animation was causing weird scrolling behavior in Chrome for
// narrow device widths, due to the initial position of the animated elements.
// This was fixed by adding an additional keyframe at the start of the animation
// that positioned the elements at the final intended location.
const fadeLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(0);
  }
  1% {
    opacity: 0;
    transform: translateX(50px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Styles = {
  title: css({
    ...Typography.sectionHeader1,
    margin: '15px 0 10px',
    color: Colors.gray,
  }),
  list: css({
    listStyleType: 'none',
    padding: '25px 0 0 0',
    display: 'block',
  }),
  item: css({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginTop: 25,
  }),
  itemNumber: (index: number) => [
    css({
      ...Typography.sectionHeader3,
      display: 'flex',
      height: 40,
      width: 40,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 20,
      background: Colors.ocean5,
      color: Colors.ocean,
      marginRight: 15,
      marginTop: 5,
      animation: `${fadeIn} ${ANIMATION_DURATION}s ease-out`,
      animationFillMode: `both`,
    }),
    withModifier(`item-number-${index}`, {
      animationDelay: `${ANIMATION_DURATION * index}s`,
    }),
  ],
  itemText: (index: number) => [
    css({
      position: 'relative',
      flex: '1 0',
      animation: `${fadeLeft} ${ANIMATION_DURATION}s ease-out`,
      animationFillMode: `both`,
    }),
    // Animation delays are multiplied by the item's index
    // so that items appear one at a time.
    withModifier(`item-text-${index}`, {
      animationDelay: `${ANIMATION_DURATION * index}s`,
    }),
  ],
  buttons: (index: number) => [
    css({
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginTop: 48,
      animation: `${fadeLeft} ${ANIMATION_DURATION}s ease-out`,
      animationFillMode: `both`,
    }),
    withModifier(`item-text-${index}`, {
      animationDelay: `${ANIMATION_DURATION * index}s`,
    }),
  ],
  skipButton: css({
    ...Typography.paragraph3Bold,
    padding: 10,
    margin: '0 18px 0 0',
    border: `none`,
    background: `none`,
    color: Colors.gray,

    '&:hover, &:focus': {
      textDecoration: 'underline',
    },
  }),
};

const RebrandStyles = {
  itemNumber: (index: number) => [
    css({
      ...Typography.sectionHeader3Bold,
      fontSize: 22,
      display: 'flex',
      height: 40,
      width: 40,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 20,
      background: Colors.neutral700,
      color: Colors.neutral100,
      marginRight: 15,
      marginTop: 5,
      animation: `${fadeIn} ${ANIMATION_DURATION}s ease-out`,
      animationFillMode: `both`,
    }),
    withModifier(`item-number-${index}`, {
      animationDelay: `${ANIMATION_DURATION * index}s`,
    }),
  ],
  itemText: (index: number) => [
    css({
      position: 'relative',
      flex: '1 0',
      animation: `${fadeLeft} ${ANIMATION_DURATION}s ease-out`,
      animationFillMode: `both`,
    }),
    // Animation delays are multiplied by the item's index
    // so that items appear one at a time.
    withModifier(`item-text-${index}`, {
      animationDelay: `${ANIMATION_DURATION * index}s`,
    }),
  ],
  buttons: (index: number) => [
    css({
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginTop: 48,
      animation: `${fadeLeft} ${ANIMATION_DURATION}s ease-out`,
      animationFillMode: `both`,
    }),
    withModifier(`item-text-${index}`, {
      animationDelay: `${ANIMATION_DURATION * index}s`,
    }),
  ],
};

const IntListItem = ({ title, subtitle, index }: IntListItemProps) => {
  return (
    <li css={Styles.item} key={title}>
      <span css={RebrandStyles.itemNumber(index)} role="presentation">
        {index}
      </span>
      <span css={RebrandStyles.itemText(index)}>
        <Text tag="p" type="paragraph1ShortBold">
          {title}
        </Text>
        <Text color={'black'} tag="p" type="paragraph3">
          {subtitle}
        </Text>
      </span>
    </li>
  );
};

const renderItem = ({ title, subtitle }: IntItem, index: number) => (
  <IntListItem
    index={index + 1}
    key={title}
    subtitle={subtitle}
    title={title}
  />
);

/** Renders the "We're finding help for you" segue screen of the Get Help form. */
const Interstitial = (props: IntProps) => {
  const {
    onClickBack,
    onClickNext,
    isPremierSupportEligible,
    onClickSkip,
    size,
  } = props;
  const listItems = buildItems(props).map(renderItem);
  return (
    <Fragment>
      <RebrandBackButton onClick={onClickBack}>Back</RebrandBackButton>
      <Spacer height={20} />
      <Section size={size} title="We’re finding help for you">
        <Spacer height={10} />
        <p>We have a few next steps for you to solve your problem</p>
        <ol css={Styles.list}>{listItems}</ol>
        <div css={RebrandStyles.buttons(listItems.length + 1)}>
          <Grid columns={isPremierSupportEligible ? 2 : 1} gap="0px 20px">
            {isPremierSupportEligible && (
              <Button color="secondary" onClick={onClickSkip}>
                Skip to Support
              </Button>
            )}
            <Button color="primary" disabled={false} onClick={onClickNext}>
              Next
            </Button>
          </Grid>
        </div>
      </Section>
    </Fragment>
  );
};

export default Interstitial;
